// src/pages/StudentCheckout.jsx
//
// Main page for the Student Checkout tool. Tabs:
//   1. My Pending     — records with at least one section pending in a
//                        category I'm authorized for (default for non-admins)
//   2. All Students   — all current-year records, filterable
//   3. Find Student   — search /students collection to start a new checkout
//   4. Rosters        — roster admin view (CSV upload)
//   5. Archive        — past school years (read-only)
//
// MS support:
//   This component is used by both /tools/student-checkout (US default view)
//   and /tools/ms-checkout (MS-scoped wrapper). The wrapper passes:
//     defaultGradeFilter: 'ms'   — initial grade filter scoped to grades 7-8
//     hideGradeFilter:    true   — suppresses the dropdown so MS coordinators
//                                  can't accidentally widen the view
//     pageTitle:          'MS Student Checkout' — header text override
//
// These props are forwarded to CheckoutDashboardTable. When unset, behavior
// matches the original /tools/student-checkout view exactly.

import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  query,
  where,
  onSnapshot,
  getDoc,
  getDocs,
  orderBy,
  limit,
} from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../config/firebase';

import {
  RECORD_STATUS,
  DEFAULT_SCHOOL_YEAR,
  buildCheckoutRecordId,
  getMyPendingSectionsInRecord,
  filterRecordsByTeacherMatching,
  hasAnyCheckoutPermission,
  hasAnyRosterAdminRights,
  getMyRosterAdminSections,
  getSectionConfig,
  MS_GRADES,
  US_GRADES,
  KA_COLORS,
} from '../utils/checkoutHelpers';
import { canAccessTool } from '../config/roles';

import CheckoutDashboardTable from '../components/CheckoutDashboardTable';
import CheckoutStudentDetail from '../components/CheckoutStudentDetail';
import CheckoutCsvUploadCard from '../components/CheckoutCsvUploadCard';
import CheckoutBatchPrintModal from '../components/CheckoutBatchPrintModal';
import EditRecordSectionsModal from '../components/EditRecordSectionsModal';

import { useAuth } from '../contexts/AuthContext';

// -----------------------------------------------------------------------------

const styles = {
  page: {
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
    color: '#222',
    maxWidth: 1300,
    margin: '0 auto',
    padding: '16px 20px',
  },
  pageHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 4,
    flexWrap: 'wrap',
  },
  pageTitle: {
    fontFamily: 'Georgia, serif',
    fontSize: 28,
    fontWeight: 700,
    color: KA_COLORS.navy,
    margin: 0,
  },
  yearBadge: {
    background: KA_COLORS.gold,
    color: '#fff',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
  },
  scopeBadge: {
    background: KA_COLORS.navy,
    color: '#fff',
    padding: '3px 10px',
    borderRadius: 12,
    fontSize: 12,
    fontWeight: 700,
  },
  pageSubtitle: {
    fontSize: 13,
    color: '#666',
    margin: '0 0 16px',
  },
  tabBar: {
    display: 'flex',
    gap: 4,
    marginBottom: 16,
    borderBottom: `2px solid ${KA_COLORS.borderGray}`,
    flexWrap: 'wrap',
  },
  tab: (active) => ({
    padding: '10px 18px',
    background: 'none',
    border: 'none',
    borderBottom: `3px solid ${active ? KA_COLORS.red : 'transparent'}`,
    cursor: 'pointer',
    fontSize: 14,
    fontWeight: active ? 700 : 500,
    color: active ? KA_COLORS.navy : '#666',
    fontFamily: 'inherit',
    transition: 'border-color 0.15s, color 0.15s',
    marginBottom: -2,
  }),
  badge: {
    display: 'inline-block',
    background: KA_COLORS.red,
    color: '#fff',
    fontSize: 10,
    fontWeight: 700,
    padding: '1px 6px',
    borderRadius: 8,
    marginLeft: 6,
    verticalAlign: 'middle',
  },

  findWrapper: {
    background: '#fff',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
    padding: 16,
  },
  findInput: {
    width: '100%',
    padding: '10px 12px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 14,
    boxSizing: 'border-box',
    marginBottom: 12,
  },
  findHint: { fontSize: 12, color: '#888', marginBottom: 12 },
  studentList: {
    listStyle: 'none',
    padding: 0,
    margin: 0,
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    maxHeight: 480,
    overflowY: 'auto',
  },
  studentItem: {
    padding: '10px 14px',
    borderBottom: `1px solid ${KA_COLORS.borderGray}`,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    cursor: 'pointer',
    transition: 'background 0.1s',
  },
  studentItemHover: { background: '#FFF5F5' },
  studentItemMeta: { fontSize: 11, color: '#888' },
  studentItemRight: { display: 'flex', alignItems: 'center', gap: 8 },
  hasRecordPill: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 3,
    background: '#D4EDDA',
    color: '#155724',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },
  noRecordPill: {
    fontSize: 10,
    fontWeight: 700,
    padding: '2px 6px',
    borderRadius: 3,
    background: '#FFE0B2',
    color: '#E65100',
    textTransform: 'uppercase',
    letterSpacing: 0.4,
  },

  loadingState: {
    padding: 40,
    textAlign: 'center',
    color: '#666',
    fontStyle: 'italic',
  },
  noPermissions: {
    padding: 40,
    textAlign: 'center',
    background: '#FFF8E1',
    border: '1px solid #FFE082',
    borderRadius: 6,
    color: '#5D4037',
    margin: '20px 0',
  },
  archivePicker: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    marginBottom: 14,
    padding: 12,
    background: KA_COLORS.lightGray,
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 6,
  },
  archiveLabel: { fontSize: 13, fontWeight: 600, color: '#333' },
  archiveSelect: {
    padding: '6px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`,
    borderRadius: 4,
    fontSize: 13,
    background: '#fff',
  },
};

// =============================================================================
// Helpers
// =============================================================================

// Filter a record list down to the active scope (used by the MS-scoped
// wrapper). When scope='ms' we keep grades 7-8; when 'us' we keep 9-12;
// when 'all' or unrecognized, no filtering.
function scopeFilterRecords(records, scope) {
  if (scope === 'ms') return records.filter((r) => MS_GRADES.includes(r.grade));
  if (scope === 'us') return records.filter((r) => US_GRADES.includes(r.grade));
  return records;
}

function scopeFilterStudents(students, scope) {
  if (scope === 'ms') return students.filter((s) => MS_GRADES.includes(s.grade));
  if (scope === 'us') return students.filter((s) => US_GRADES.includes(s.grade));
  return students;
}

// =============================================================================
// Find Student tab
// =============================================================================

const FindStudentTab = ({ user, currentSchoolYear, onPickStudent, scope }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recordIds, setRecordIds] = useState(new Set());

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        if (cancelled) return;
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const an = `${a.lastName || ''} ${a.firstName || ''}`.toLowerCase();
          const bn = `${b.lastName || ''} ${b.firstName || ''}`.toLowerCase();
          return an.localeCompare(bn);
        });
        setStudents(list);
      } catch (err) {
        console.error('Load students failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!currentSchoolYear) return undefined;
    const q = query(
      collection(db, 'checkoutRecords'),
      where('schoolYear', '==', currentSchoolYear),
    );
    const unsub = onSnapshot(q, (snap) => {
      const ids = new Set();
      snap.forEach((d) => {
        const data = d.data();
        if (data.studentId) ids.add(String(data.studentId));
      });
      setRecordIds(ids);
    });
    return () => unsub();
  }, [currentSchoolYear]);

  // Apply MS/US scope on top of search filtering
  const scopedStudents = useMemo(
    () => scopeFilterStudents(students, scope),
    [students, scope],
  );

  const filtered = useMemo(() => {
    if (!searchTerm.trim()) return scopedStudents.slice(0, 50);
    const q = searchTerm.trim().toLowerCase();
    return scopedStudents
      .filter((s) => {
        const name = `${s.firstName || ''} ${s.lastName || ''} ${s.name || ''}`.toLowerCase();
        const sis = String(s.sisId || s.id || '').toLowerCase();
        return name.includes(q) || sis.includes(q);
      })
      .slice(0, 100);
  }, [scopedStudents, searchTerm]);

  const scopeLabel = scope === 'ms'
    ? 'middle school students'
    : scope === 'us'
    ? 'upper school students'
    : 'students';

  return (
    <div style={styles.findWrapper}>
      <input
        type="text"
        style={styles.findInput}
        placeholder={`Search ${scopeLabel} by name or SIS ID…`}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
      />
      <p style={styles.findHint}>
        {loading
          ? 'Loading students…'
          : `${scopedStudents.length} ${scopeLabel} total. Click a student to view their checkout record (or create one if needed).`}
      </p>

      {!loading && (
        <ul style={styles.studentList}>
          {filtered.length === 0 ? (
            <li style={{ ...styles.studentItem, color: '#999', fontStyle: 'italic' }}>
              No students match your search.
            </li>
          ) : (
            filtered.map((student) => (
              <FindStudentRow
                key={student.id}
                student={student}
                hasRecord={recordIds.has(String(student.sisId || student.id))}
                onPick={() => onPickStudent(student)}
              />
            ))
          )}
        </ul>
      )}
    </div>
  );
};

const FindStudentRow = ({ student, hasRecord, onPick }) => {
  const [hover, setHover] = useState(false);
  const fullName =
    student.name ||
    `${student.firstName || ''} ${student.lastName || ''}`.trim() ||
    'Unknown';
  return (
    <li
      style={{
        ...styles.studentItem,
        ...(hover ? styles.studentItemHover : {}),
      }}
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      onClick={onPick}
    >
      <div>
        <div style={{ fontWeight: 600, color: KA_COLORS.navy }}>{fullName}</div>
        <div style={styles.studentItemMeta}>
          {[student.grade, student.cohort, student.sisId && `SIS ${student.sisId}`]
            .filter(Boolean)
            .join('  •  ')}
        </div>
      </div>
      <div style={styles.studentItemRight}>
        {hasRecord ? (
          <span style={styles.hasRecordPill}>Has record</span>
        ) : (
          <span style={styles.noRecordPill}>No record yet</span>
        )}
      </div>
    </li>
  );
};

// =============================================================================
// Rosters tab — CSV upload + roster view per section
// =============================================================================

const RostersTab = ({ user, currentSchoolYear }) => {
  const [rosters, setRosters] = useState({});
  const [studentsById, setStudentsById] = useState({});
  const [loading, setLoading] = useState(true);
  const [activeSectionId, setActiveSectionId] = useState(null);

  const adminSections = useMemo(() => getMyRosterAdminSections(user), [user]);

  useEffect(() => {
    if (!currentSchoolYear) return undefined;
    const q = query(
      collection(db, 'checkoutRosters'),
      where('schoolYear', '==', currentSchoolYear),
    );
    const unsub = onSnapshot(q, (snap) => {
      const map = {};
      snap.forEach((d) => {
        const data = d.data();
        if (data.sectionId) map[data.sectionId] = data;
      });
      setRosters(map);
      setLoading(false);
    });
    return () => unsub();
  }, [currentSchoolYear]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const snap = await getDocs(collection(db, 'students'));
        if (cancelled) return;
        const map = {};
        snap.forEach((d) => { map[d.id] = { id: d.id, ...d.data() }; });
        setStudentsById(map);
      } catch (err) {
        console.error('load students for rosters tab failed:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (adminSections.length === 0) {
    return (
      <div
        style={{
          padding: 30,
          textAlign: 'center',
          background: KA_COLORS.lightGray,
          borderRadius: 6,
          color: '#666',
          fontSize: 14,
        }}
      >
        You don't have roster-admin rights for any sections. Ask a superadmin
        to grant you roster-admin access from the Super Admin panel.
      </div>
    );
  }

  const focusSectionId = activeSectionId || adminSections[0];
  const focusRoster = rosters[focusSectionId];
  const focusSectionCfg = getSectionConfig(focusSectionId);
  const focusSisIds = focusRoster?.sisIds || [];

  return (
    <div>
      <div
        style={{
          background: '#fff',
          border: `1px solid ${KA_COLORS.borderGray}`,
          borderRadius: 6,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <p
          style={{
            margin: '0 0 8px',
            fontSize: 13,
            color: '#444',
            lineHeight: 1.5,
          }}
        >
          You can manage rosters for the following sections. Pick one to view
          the current roster, then upload a CSV below to update it.
        </p>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
          {adminSections.map((sid) => {
            const cfg = getSectionConfig(sid);
            const isActive = sid === focusSectionId;
            const count = rosters[sid]?.sisIds?.length || 0;
            return (
              <button
                key={sid}
                type="button"
                onClick={() => setActiveSectionId(sid)}
                style={{
                  padding: '6px 12px',
                  border: `2px solid ${isActive ? KA_COLORS.red : KA_COLORS.borderGray}`,
                  background: isActive ? '#FFF5F5' : '#fff',
                  borderRadius: 4,
                  cursor: 'pointer',
                  fontSize: 13,
                  fontWeight: isActive ? 700 : 500,
                  color: isActive ? KA_COLORS.navy : '#444',
                }}
              >
                {cfg?.label || sid} <span style={{ color: '#888', fontWeight: 400 }}>({count})</span>
              </button>
            );
          })}
        </div>
      </div>

      <div
        style={{
          background: '#fff',
          border: `1px solid ${KA_COLORS.borderGray}`,
          borderRadius: 6,
          padding: 14,
          marginBottom: 14,
        }}
      >
        <h3 style={{ margin: '0 0 4px', fontSize: 15, color: KA_COLORS.navy }}>
          {focusSectionCfg?.label || focusSectionId} — current roster
        </h3>
        {loading ? (
          <p style={{ fontSize: 12, color: '#666', margin: 0 }}>Loading…</p>
        ) : focusSisIds.length === 0 ? (
          <p style={{ fontSize: 12, color: '#666', margin: 0, fontStyle: 'italic' }}>
            No students on this roster yet. Upload a CSV below to populate it.
          </p>
        ) : (
          <>
            <p style={{ fontSize: 12, color: '#666', margin: '0 0 10px' }}>
              {focusSisIds.length} student{focusSisIds.length === 1 ? '' : 's'} on this roster
              {focusRoster?.updatedByName && (
                <> — last updated by <strong>{focusRoster.updatedByName}</strong></>
              )}
            </p>
            <div
              style={{
                maxHeight: 240,
                overflowY: 'auto',
                border: `1px solid ${KA_COLORS.borderGray}`,
                borderRadius: 4,
                padding: 8,
                fontSize: 12,
                color: '#333',
              }}
            >
              {focusSisIds.slice(0, 200).map((sisId) => {
                const stu = studentsById[sisId];
                return (
                  <div key={sisId} style={{ padding: '2px 0' }}>
                    <code>{sisId}</code>
                    {stu && (
                      <> — {stu.name || `${stu.firstName || ''} ${stu.lastName || ''}`.trim()}{' '}
                        <span style={{ color: '#888' }}>
                          ({stu.grade}{stu.cohort ? ` / ${stu.cohort}` : ''})
                        </span>
                      </>
                    )}
                  </div>
                );
              })}
              {focusSisIds.length > 200 && (
                <p style={{ fontSize: 11, color: '#888', fontStyle: 'italic', margin: '6px 0 0' }}>
                  …and {focusSisIds.length - 200} more
                </p>
              )}
            </div>
          </>
        )}
      </div>

      <CheckoutCsvUploadCard user={user} defaultSectionId={focusSectionId} />
    </div>
  );
};

// =============================================================================
// Main page
// =============================================================================

/**
 * StudentCheckout
 *
 * Optional props (used by the MS Checkout wrapper):
 *   defaultGradeFilter: 'ms' | 'us' | 'all' | specific grade string.
 *     Initial value of the dashboard's grade filter. Defaults to 'all'.
 *     When 'ms' or 'us', also scopes the Find Student tab and the
 *     subscription so MS coordinators don't pull all US records.
 *   hideGradeFilter: boolean. Hide the grade dropdown entirely (MS view).
 *   pageTitle: string. Override the "Student Checkout" header.
 */
const StudentCheckout = ({
  defaultGradeFilter = 'all',
  hideGradeFilter = false,
  pageTitle = 'Student Checkout',
} = {}) => {
  const auth = useAuth();
  const user = useMemo(() => {
    if (!auth?.user) return null;
    const a = auth.user;
    const p = auth.profile || {};
    return {
      uid: a.uid,
      email: a.email || p.email || null,
      displayName: a.displayName || p.displayName || null,
      photoURL: a.photoURL || p.photoURL || null,
      role: p.role || null,
      allowedTools: Array.isArray(p.allowedTools) ? p.allowedTools : [],
      checkoutSections: Array.isArray(p.checkoutSections) ? p.checkoutSections : [],
      checkoutRosterAdminSections: Array.isArray(p.checkoutRosterAdminSections)
        ? p.checkoutRosterAdminSections
        : [],
      checkoutOverrides: Array.isArray(p.checkoutOverrides) ? p.checkoutOverrides : [],
      staffSisId: p.staffSisId ? String(p.staffSisId) : null,
      isSuperAdmin: !!auth.isSuperAdmin,
      isStudent: !!auth.isStudent, 
    };
  }, [auth?.user, auth?.profile, auth?.isSuperAdmin]);

  // Determine the scope from defaultGradeFilter. We use this for:
  //   1. Pre-filtering records (My Pending, All Students) so MS coordinators
  //      don't see US records bleeding through, even if they have access to
  //      both via shared signoff sections.
  //   2. Scoping the Find Student tab.
  //   3. Scoping the batch print visible-records snapshot.
  // The dashboard's own grade filter (in CheckoutDashboardTable) still
  // controls the visible rows within the scope.
  const scope = useMemo(() => {
    if (defaultGradeFilter === 'ms') return 'ms';
    if (defaultGradeFilter === 'us') return 'us';
    return 'all';
  }, [defaultGradeFilter]);

  const [systemSettings, setSystemSettings] = useState(null);
  const [records, setRecords] = useState([]);
  const [recordsLoading, setRecordsLoading] = useState(true);

  const defaultTab = (() => {
    if (user?.isSuperAdmin) return 'all';
    if (Array.isArray(user?.checkoutSections) && user.checkoutSections.length > 0) {
      return 'my-pending';
    }
    return 'all';
  })();
  const [tab, setTab] = useState(defaultTab);

  const [activeRecordId, setActiveRecordId] = useState(null);

  const [creatingRecord, setCreatingRecord] = useState(false);
  const [createError, setCreateError] = useState(null);

  const [archiveYear, setArchiveYear] = useState(null);
  const [archiveRecords, setArchiveRecords] = useState([]);
  const [archiveLoading, setArchiveLoading] = useState(false);

  const [batchPrintOpen, setBatchPrintOpen] = useState(false);
  const [visibleAllStudents, setVisibleAllStudents] = useState([]);

  useEffect(() => {
    const ref = doc(db, 'config', 'systemSettings');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        if (!snap.exists()) {
          setSystemSettings({
            currentSchoolYear: DEFAULT_SCHOOL_YEAR,
            archivedSchoolYears: [],
          });
        } else {
          setSystemSettings(snap.data());
        }
      },
      (err) => {
        console.error('systemSettings subscribe failed:', err);
        setSystemSettings({
          currentSchoolYear: DEFAULT_SCHOOL_YEAR,
          archivedSchoolYears: [],
        });
      },
    );
    return () => unsub();
  }, []);

  const currentSchoolYear = systemSettings?.currentSchoolYear;

  useEffect(() => {
    if (!currentSchoolYear) return undefined;
    setRecordsLoading(true);
    const q = query(
      collection(db, 'checkoutRecords'),
      where('schoolYear', '==', currentSchoolYear),
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) => {
          const an = (a.studentName || '').toLowerCase();
          const bn = (b.studentName || '').toLowerCase();
          return an.localeCompare(bn);
        });
        setRecords(list);
        setRecordsLoading(false);
      },
      (err) => {
        console.error('records subscribe failed:', err);
        setRecordsLoading(false);
      },
    );
    return () => unsub();
  }, [currentSchoolYear]);

  useEffect(() => {
    if (!archiveYear) return;
    setArchiveLoading(true);
    const load = async () => {
      try {
        const q = query(
          collection(db, 'checkoutRecords'),
          where('schoolYear', '==', archiveYear),
        );
        const snap = await getDocs(q);
        const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
        list.sort((a, b) =>
          (a.studentName || '').toLowerCase().localeCompare((b.studentName || '').toLowerCase()),
        );
        setArchiveRecords(list);
      } catch (err) {
        console.error('archive load failed:', err);
        setArchiveRecords([]);
      } finally {
        setArchiveLoading(false);
      }
    };
    load();
  }, [archiveYear]);

  // Pre-filter records by scope (MS coordinators never see US records)
  const scopedRecords = useMemo(
    () => scopeFilterRecords(records, scope),
    [records, scope],
  );
  const scopedArchiveRecords = useMemo(
    () => scopeFilterRecords(archiveRecords, scope),
    [archiveRecords, scope],
  );

  // My Pending: derived from scoped records
  const myPendingRecords = useMemo(() => {
    if (!user) return [];
    const broadMatches = scopedRecords.filter((r) => {
      const pending = getMyPendingSectionsInRecord(r, user);
      return pending.length > 0;
    });
    return filterRecordsByTeacherMatching(broadMatches, user);
  }, [scopedRecords, user]);

  const myPendingCount = myPendingRecords.length;

  const handlePickStudent = async (student) => {
    if (!currentSchoolYear) return;
    const sisId = student.sisId || student.id;
    const recordId = buildCheckoutRecordId(currentSchoolYear, sisId);
    setCreateError(null);
    try {
      const ref = doc(db, 'checkoutRecords', recordId);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setActiveRecordId(recordId);
        return;
      }
      const hasAnyRights = canSignAnything || hasAnyRosterAdminRights(user);
      if (!hasAnyRights) {
        alert(
          "This student doesn't have a checkout record yet. Only users " +
          'assigned to a signoff section or roster admin role can create ' +
          'new records. Ask a superadmin to assign you, or have an ' +
          'authorized colleague start the checkout.',
        );
        return;
      }
      setCreatingRecord(true);
      const functions = getFunctions();
      const fn = httpsCallable(functions, 'assignCheckoutForm');
      const result = await fn({
        studentId: String(sisId),
        schoolYear: currentSchoolYear,
      });
      const newRecordId = result?.data?.recordId || recordId;
      setActiveRecordId(newRecordId);
    } catch (err) {
      console.error('handlePickStudent failed:', err);
      setCreateError(err.message || 'Failed to create record.');
      alert(`Failed to create record: ${err.message || 'Unknown error.'}`);
    } finally {
      setCreatingRecord(false);
    }
  };

  if (!user) {
    return (
      <div style={styles.page}>
        <div style={styles.loadingState}>Loading…</div>
      </div>
    );
  }

  // Both /tools/student-checkout and /tools/ms-checkout require tool access.
  // We check whichever tool ID matches the scope so the access gate works
  // for users granted only one of the two tools.
  const requiredToolId = scope === 'ms' ? 'ms_checkout' : 'student_checkout';
  if (!canAccessTool(requiredToolId, user)) {
    return (
      <div style={styles.page}>
        <div style={styles.noPermissions}>
          <strong>You don't have access to {pageTitle}.</strong>
          <p style={{ margin: '6px 0 0', fontSize: 13 }}>
            A superadmin must grant you access to this tool, or assign you
            to at least one signoff section (IT, Library, etc.).
          </p>
        </div>
      </div>
    );
  }

  const canSignAnything = hasAnyCheckoutPermission(user);

  if (activeRecordId) {
    return (
      <CheckoutStudentDetail
        recordId={activeRecordId}
        user={user}
        onBack={() => setActiveRecordId(null)}
      />
    );
  }

  // Scope badge text for the header
  const scopeBadgeText = scope === 'ms'
    ? 'Grades 7–8'
    : scope === 'us'
    ? 'Grades 9–12'
    : null;

  return (
    <div style={styles.page}>
      <div style={styles.pageHeader}>
        <h1 style={styles.pageTitle}>{pageTitle}</h1>
        {scopeBadgeText && (
          <span style={styles.scopeBadge}>{scopeBadgeText}</span>
        )}
        {currentSchoolYear && (
          <span style={styles.yearBadge}>{currentSchoolYear}</span>
        )}
      </div>
      <p style={styles.pageSubtitle}>
        Track and sign off on students' end-of-year exit checklists.
      </p>

      <div style={styles.tabBar}>
        {canSignAnything && (
          <button
            type="button"
            style={styles.tab(tab === 'my-pending')}
            onClick={() => setTab('my-pending')}
          >
            My Pending
            {myPendingCount > 0 && (
              <span style={styles.badge}>{myPendingCount}</span>
            )}
          </button>
        )}
        <button
          type="button"
          style={styles.tab(tab === 'all')}
          onClick={() => setTab('all')}
        >
          All Students
        </button>
        <button
          type="button"
          style={styles.tab(tab === 'find')}
          onClick={() => setTab('find')}
        >
          Find Student
        </button>
        {hasAnyRosterAdminRights(user) && (
          <button
            type="button"
            style={styles.tab(tab === 'rosters')}
            onClick={() => setTab('rosters')}
          >
            Rosters
          </button>
        )}
        <button
          type="button"
          style={styles.tab(tab === 'archive')}
          onClick={() => setTab('archive')}
        >
          Archive
        </button>
      </div>

      {/* My Pending */}
      {tab === 'my-pending' && (
        recordsLoading ? (
          <div style={styles.loadingState}>Loading…</div>
        ) : (
          <CheckoutDashboardTable
            records={myPendingRecords}
            user={user}
            onRowClick={(r) => setActiveRecordId(r.id)}
            defaultGradeFilter={defaultGradeFilter}
            hideGradeFilter={hideGradeFilter}
            emptyMessage="🎉 No students currently waiting on you. Nice work!"
          />
        )
      )}

      {/* All Students */}
      {tab === 'all' && (
        recordsLoading ? (
          <div style={styles.loadingState}>Loading…</div>
        ) : (
          <CheckoutDashboardTable
            records={scopedRecords}
            user={user}
            onRowClick={(r) => setActiveRecordId(r.id)}
            onVisibleRecordsChange={setVisibleAllStudents}
            defaultGradeFilter={defaultGradeFilter}
            hideGradeFilter={hideGradeFilter}
            toolbarExtra={
              user?.isSuperAdmin ? (
                <button
                  type="button"
                  onClick={() => setBatchPrintOpen(true)}
                  style={{
                    padding: '6px 12px',
                    border: '1px solid var(--ka-navy)',
                    background: 'var(--ka-navy)',
                    color: '#fff',
                    borderRadius: 4,
                    cursor: 'pointer',
                    fontSize: 13,
                    fontWeight: 600,
                  }}
                  title="Generate one big PDF with every visible student's checkout sheet"
                >
                  Print all (PDF)
                </button>
              ) : null
            }
            emptyMessage="No checkout records have been created yet for this school year."
          />
        )
      )}

      {/* Find Student */}
      {tab === 'find' && (
        <FindStudentTab
          user={user}
          currentSchoolYear={currentSchoolYear}
          onPickStudent={handlePickStudent}
          scope={scope}
        />
      )}

      {/* Rosters */}
      {tab === 'rosters' && hasAnyRosterAdminRights(user) && (
        <RostersTab user={user} currentSchoolYear={currentSchoolYear} />
      )}

      {/* Archive */}
      {tab === 'archive' && (
        <ArchiveTab
          systemSettings={systemSettings}
          archiveYear={archiveYear}
          setArchiveYear={setArchiveYear}
          archiveRecords={scopedArchiveRecords}
          archiveLoading={archiveLoading}
          user={user}
          onRowClick={(r) => setActiveRecordId(r.id)}
          defaultGradeFilter={defaultGradeFilter}
          hideGradeFilter={hideGradeFilter}
        />
      )}

      {batchPrintOpen && (
        <CheckoutBatchPrintModal
          records={visibleAllStudents}
          onClose={() => setBatchPrintOpen(false)}
        />
      )}
    </div>
  );
};

// -----------------------------------------------------------------------------

const ArchiveTab = ({
  systemSettings,
  archiveYear,
  setArchiveYear,
  archiveRecords,
  archiveLoading,
  user,
  onRowClick,
  defaultGradeFilter,
  hideGradeFilter,
}) => {
  const archivedYears = systemSettings?.archivedSchoolYears || [];

  if (archivedYears.length === 0) {
    return (
      <div style={styles.noPermissions}>
        <strong>No archived years yet.</strong>
        <p style={{ margin: '6px 0 0', fontSize: 13 }}>
          Past school years will appear here once a superadmin runs "Archive
          &amp; advance" from System Settings at the end of the school year.
        </p>
      </div>
    );
  }

  return (
    <div>
      <div style={styles.archivePicker}>
        <span style={styles.archiveLabel}>School year:</span>
        <select
          style={styles.archiveSelect}
          value={archiveYear || ''}
          onChange={(e) => setArchiveYear(e.target.value || null)}
        >
          <option value="">— Select a year —</option>
          {archivedYears.map((yr) => (
            <option key={yr} value={yr}>
              {yr}
            </option>
          ))}
        </select>
        {archiveYear && (
          <span style={{ fontSize: 12, color: '#666' }}>
            {archiveRecords.length} record
            {archiveRecords.length === 1 ? '' : 's'}
          </span>
        )}
      </div>

      {!archiveYear && (
        <div style={{ ...styles.loadingState, fontStyle: 'normal' }}>
          Choose a school year above to view archived records.
        </div>
      )}

      {archiveYear &&
        (archiveLoading ? (
          <div style={styles.loadingState}>Loading archive…</div>
        ) : (
          <CheckoutDashboardTable
            records={archiveRecords}
            user={user}
            onRowClick={onRowClick}
            defaultGradeFilter={defaultGradeFilter}
            hideGradeFilter={hideGradeFilter}
            emptyMessage="No records found for this archived year."
          />
        ))}
    </div>
  );
};

export default StudentCheckout;